/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { sendRequest } from '../../utils/dateHelpers';
import AssistantArtifactRenderer from '../../components/Assistant/AssistantArtifactRenderer';
import {
  DEFAULT_ASSISTANT_STARTERS,
  extractAssistantData,
  makeAssistantMessage as makeSharedAssistantMessage,
  makeAssistantUserMessage,
  mergeAssistantStarters,
  getAssistantMessageEndpoint,
  MINI_ASSISTANT_SESSION_STORAGE_KEY
} from '../../utils/aiAssistantClient';

export const DEFAULT_STARTERS = DEFAULT_ASSISTANT_STARTERS;

export const HISTORY_PIN_STORAGE_KEY = 'aiAssistantHistoryPinned';
const STARTER_ROTATION_INTERVAL = 6000;

export const mergeStarterCatalog = serverStarters =>
  mergeAssistantStarters(serverStarters, DEFAULT_STARTERS);

const extractData = extractAssistantData;
const makeUserMessage = makeAssistantUserMessage;
const makeAssistantMessage = makeSharedAssistantMessage;

const buildSessionsUrl = (scope, search) => {
  const params = new URLSearchParams({
    scope
  });
  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    params.set('search', trimmedSearch);
  }
  return `/ai-assistant/sessions?${params.toString()}`;
};

const StarterCards = ({ starters, onStarterClick, onInteractionChange }) => (
  <div
    className="mt-8 grid w-full max-w-4xl gap-3 text-left md:grid-cols-3"
    onMouseEnter={() => onInteractionChange(true)}
    onMouseLeave={() => onInteractionChange(false)}
    onFocusCapture={() => onInteractionChange(true)}
    onBlurCapture={event => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        onInteractionChange(false);
      }
    }}
  >
    {starters.map((starter, index) => (
      <button
        key={`${index}-${starter}`}
        type="button"
        className="min-h-[104px] rounded-xl border border-stroke bg-white p-4 text-left text-sm leading-6 text-black shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-strokedark dark:bg-boxdark dark:text-white"
        onClick={() => onStarterClick(starter)}
      >
        <span className="mb-3 flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icons.AutoAwesome fontSize="inherit" />
        </span>
        {starter}
      </button>
    ))}
  </div>
);

const MessageComposer = ({
  input,
  inputRef,
  canSend,
  onInputChange,
  onKeyDown,
  onSubmit,
  hero = false
}) => (
  <form onSubmit={onSubmit} className={hero ? 'w-full max-w-4xl' : ''}>
    <label className="sr-only" htmlFor="ai-assistant-message">
      Message Ask AI
    </label>
    <div
      className={`flex items-end gap-3 rounded-xl border border-stroke bg-white p-2 shadow-sm transition focus-within:border-primary focus-within:shadow-md dark:border-strokedark dark:bg-form-input ${
        hero ? 'mt-7' : ''
      }`}
    >
      <textarea
        id="ai-assistant-message"
        ref={inputRef}
        value={input}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        rows={hero ? 3 : 2}
        placeholder="Ask about engineering effort, AI leverage, retention, developers, repositories..."
        className={`min-w-0 flex-1 resize-none bg-transparent px-3 py-2 text-sm leading-6 text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark ${
          hero ? 'min-h-[96px]' : 'min-h-[64px]'
        }`}
      />
      <button
        type="submit"
        disabled={!canSend}
        className="mb-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Send message"
        title="Send message"
      >
        <Icons.Send fontSize="small" />
      </button>
    </div>
    {hero && (
      <p className="mt-3 text-center text-xs text-body dark:text-bodydark">
        Press Enter to send. Shift + Enter adds a new line.
      </p>
    )}
  </form>
);

const EmptyConversation = ({
  starters,
  onStarterClick,
  onStarterInteractionChange,
  composer
}) => (
  <div className="flex h-full min-h-[520px] flex-col items-center justify-center px-4 py-10 text-center sm:px-8">
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">
      <Icons.AutoAwesome fontSize="medium" />
    </div>
    <p className="mt-6 text-sm font-semibold uppercase tracking-[0.18em] text-primary">
      GitMe Ask AI
    </p>
    <h2 className="mt-3 max-w-3xl text-3xl font-semibold tracking-tight text-black dark:text-white sm:text-4xl">
      What would you like to understand about your engineering team?
    </h2>
    <p className="mt-4 max-w-2xl text-sm leading-6 text-body dark:text-bodydark">
      Gitzy turns your GitMe data into clear answers, trends, comparisons, and
      direct paths to the right workspace.
    </p>
    {composer}
    <StarterCards
      starters={starters}
      onStarterClick={onStarterClick}
      onInteractionChange={onStarterInteractionChange}
    />
  </div>
);

const SessionList = ({
  sessions,
  activeSessionId,
  loading,
  search,
  scope,
  pinned,
  onSearchChange,
  onScopeChange,
  onSelectSession,
  onCreateSession,
  onRenameSession,
  onDeleteSession,
  onClose,
  onTogglePin
}) => (
  <aside
    className="flex h-full min-h-[640px] w-full flex-col border-r border-stroke bg-white dark:border-strokedark dark:bg-boxdark"
    aria-label="Conversation history"
  >
    <div className="flex items-center justify-between gap-3 border-b border-stroke px-4 py-3 dark:border-strokedark">
      <div>
        <p className="text-sm font-semibold text-black dark:text-white">
          Conversations
        </p>
        <p className="text-xs text-body dark:text-bodydark">Ask AI history</p>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition dark:border-strokedark ${
            pinned
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-stroke text-body hover:border-primary hover:text-primary dark:text-bodydark'
          }`}
          onClick={onTogglePin}
          title={
            pinned ? 'Unpin conversation history' : 'Pin conversation history'
          }
          aria-label={
            pinned ? 'Unpin conversation history' : 'Pin conversation history'
          }
          aria-pressed={pinned}
        >
          {pinned ? (
            <Icons.PushPin fontSize="small" />
          ) : (
            <Icons.PushPinOutlined fontSize="small" />
          )}
        </button>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-stroke text-body transition hover:border-primary hover:text-primary dark:border-strokedark dark:text-bodydark"
          onClick={onCreateSession}
          title="New conversation"
          aria-label="New conversation"
        >
          <Icons.Add fontSize="small" />
        </button>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-body transition hover:bg-gray-2 hover:text-primary dark:text-bodydark dark:hover:bg-boxdark-2"
          onClick={onClose}
          title="Close conversation history"
          aria-label="Close conversation history"
        >
          <Icons.Close fontSize="small" />
        </button>
      </div>
    </div>

    <div className="space-y-3 border-b border-stroke p-3 dark:border-strokedark">
      <div className="grid grid-cols-2 rounded-lg border border-stroke bg-gray-2 p-1 dark:border-strokedark dark:bg-boxdark-2">
        {[
          { key: 'mine', label: 'Mine' },
          { key: 'team', label: 'Team' }
        ].map(option => (
          <button
            key={option.key}
            type="button"
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              scope === option.key
                ? 'bg-white text-primary shadow-sm dark:bg-boxdark'
                : 'text-body hover:text-primary dark:text-bodydark'
            }`}
            onClick={() => onScopeChange(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <label className="sr-only" htmlFor="ai-assistant-session-search">
        Search conversations
      </label>
      <div className="flex items-center gap-2 rounded-lg border border-stroke bg-white px-3 py-2 dark:border-strokedark dark:bg-form-input">
        <Icons.Search
          fontSize="small"
          className="text-body dark:text-bodydark"
        />
        <input
          id="ai-assistant-session-search"
          value={search}
          onChange={event => onSearchChange(event.target.value)}
          placeholder="Search conversations"
          className="min-w-0 flex-1 bg-transparent text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark"
        />
      </div>
    </div>

    <div className="min-h-0 flex-1 overflow-y-auto p-3">
      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map(item => (
            <div
              key={item}
              className="h-14 animate-pulse rounded-lg bg-gray-2 dark:bg-boxdark-2"
            />
          ))}
        </div>
      )}

      {!loading && sessions.length === 0 && (
        <p className="rounded-lg border border-dashed border-stroke px-3 py-4 text-sm text-body dark:border-strokedark dark:text-bodydark">
          No conversations yet.
        </p>
      )}

      {!loading &&
        sessions.map(session => {
          const isActive = session.id === activeSessionId;
          return (
            <div
              key={session.id}
              className={`mb-2 flex w-full items-start gap-2 rounded-lg border transition ${
                isActive
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-transparent text-black hover:border-stroke hover:bg-gray-2 dark:text-white dark:hover:border-strokedark dark:hover:bg-boxdark-2'
              }`}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-start gap-3 px-3 py-3 text-left"
                onClick={() => onSelectSession(session.id)}
              >
                <Icons.ChatBubbleOutline fontSize="small" className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {session.title || 'New conversation'}
                  </span>
                  <span className="mt-1 block truncate text-xs text-body dark:text-bodydark">
                    {session.ownerName || 'Unknown user'}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-body dark:text-bodydark">
                    {session.lastMessageAt
                      ? new Date(session.lastMessageAt).toLocaleString()
                      : 'Just now'}
                  </span>
                </span>
              </button>
              {session.isOwner && (
                <span className="flex shrink-0 items-center gap-1 py-3 pr-2">
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-body hover:bg-white hover:text-primary dark:text-bodydark dark:hover:bg-boxdark"
                    title="Rename conversation"
                    aria-label="Rename conversation"
                    onClick={event => {
                      event.stopPropagation();
                      onRenameSession(session);
                    }}
                  >
                    <Icons.EditOutlined fontSize="inherit" />
                  </button>
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-body hover:bg-white hover:text-danger dark:text-bodydark dark:hover:bg-boxdark"
                    title="Delete conversation"
                    aria-label="Delete conversation"
                    onClick={event => {
                      event.stopPropagation();
                      onDeleteSession(session);
                    }}
                  >
                    <Icons.DeleteOutline fontSize="inherit" />
                  </button>
                </span>
              )}
            </div>
          );
        })}
    </div>
  </aside>
);

const getSafeLinkHref = href => {
  const trimmedHref = String(href || '').trim();
  if (/^https?:\/\//i.test(trimmedHref) || trimmedHref.startsWith('/')) {
    return trimmedHref;
  }
  return '';
};

const trimTrailingUrlPunctuation = value => {
  const match = String(value || '').match(/^(.*?)([.,;:!?]+)?$/);
  return {
    href: match?.[1] || value,
    trailing: match?.[2] || ''
  };
};

const renderPlainTextWithLinks = (text, keyPrefix, isUser) => {
  const parts = [];
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const { href, trailing } = trimTrailingUrlPunctuation(match[0]);
    const safeHref = getSafeLinkHref(href);
    if (safeHref) {
      parts.push(
        <a
          key={`${keyPrefix}-url-${match.index}`}
          href={safeHref}
          target="_blank"
          rel="noreferrer"
          className={`font-medium underline underline-offset-2 hover:opacity-80 ${
            isUser ? 'text-white' : 'text-primary'
          }`}
        >
          {href}
        </a>
      );
      if (trailing) parts.push(trailing);
    } else {
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const renderMessageContent = (content, isUser) => {
  const text = String(content || '');
  const parts = [];
  const markdownLinkRegex = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        ...renderPlainTextWithLinks(
          text.slice(lastIndex, match.index),
          `plain-${match.index}`,
          isUser
        )
      );
    }

    const safeHref = getSafeLinkHref(match[2]);
    if (safeHref) {
      parts.push(
        <a
          key={`markdown-link-${match.index}`}
          href={safeHref}
          target="_blank"
          rel="noreferrer"
          className={`font-medium underline underline-offset-2 hover:opacity-80 ${
            isUser ? 'text-white' : 'text-primary'
          }`}
        >
          {match[1]}
        </a>
      );
    } else {
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(
      ...renderPlainTextWithLinks(text.slice(lastIndex), 'tail', isUser)
    );
  }

  const linkedParts = parts.length > 0 ? parts : [text];
  return linkedParts.flatMap((part, index) => {
    if (typeof part !== 'string') return [part];
    return part
      .split(/(\*\*[^*]+\*\*)/g)
      .filter(Boolean)
      .map((piece, pieceIndex) => {
        if (piece.startsWith('**') && piece.endsWith('**')) {
          return (
            <strong key={`strong-${index}-${pieceIndex}`}>
              {piece.slice(2, -2)}
            </strong>
          );
        }
        return piece;
      });
  });
};

const AssistantMessage = ({
  message,
  onApproval,
  onPlanApproval,
  onPlanContinuation,
  approvalBusy
}) => {
  const isUser = message.role === 'user';
  const messageText = message.content || message.answer;
  const pendingAction = message.pendingAction || message.pendingApprovals?.[0];
  const actionIsProcessing = pendingAction?.status === 'processing';
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const plan = message.pendingPlan;
  const stepStatusLabel = status =>
    ({
      completed: 'tamamlandı',
      failed: 'başarısız',
      skipped: 'çalıştırılmadı',
      running: 'işleniyor',
      pending: 'bekliyor'
    })[status] || status;

  return (
    <article className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[min(860px,92%)] rounded-lg px-4 py-3 text-sm leading-6 ${
          isUser
            ? 'bg-primary text-white'
            : 'border border-stroke bg-white text-black dark:border-strokedark dark:bg-boxdark dark:text-white'
        }`}
      >
        {messageText && (
          <div className="whitespace-pre-wrap break-words">
            {renderMessageContent(messageText, isUser)}
          </div>
        )}

        {!isUser && message.screens?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.screens.map(screen => (
              <a
                key={`${message.id}-${screen.url}`}
                href={screen.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary hover:border-primary"
              >
                <Icons.OpenInNew fontSize="inherit" />
                {screen.label || screen.title || 'Open screen'}
              </a>
            ))}
          </div>
        )}
        {!isUser && pendingAction && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100">
            <p className="text-xs leading-5">
              {pendingAction.summary ||
                'Bu değişikliği uygulamak istiyor musunuz?'}
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                disabled={approvalBusy || actionIsProcessing}
                onClick={() => onApproval(message, pendingAction, true)}
                className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
              >
                {actionIsProcessing ? 'İşleniyor…' : 'Onayla'}
              </button>
              <button
                type="button"
                disabled={approvalBusy || actionIsProcessing}
                onClick={() => onApproval(message, pendingAction, false)}
                className="rounded border border-amber-400 px-2.5 py-1 text-xs font-medium disabled:opacity-50"
              >
                Vazgeç
              </button>
            </div>
          </div>
        )}
        {!isUser && plan && (
          <div className="mt-4 rounded-md border border-primary/25 bg-primary/5 px-3 py-3 text-black dark:text-white">
            <p className="text-sm font-semibold">Toplu iş planı</p>
            <p className="mt-1 text-xs leading-5 text-body dark:text-bodydark">
              {plan.summary}
            </p>
            {plan.lifecycleState && plan.status === 'pending_approval' && (
              <p className="mt-1 text-xs text-body dark:text-bodydark">
                Onaylandığında tüm değişiklikler atomik uygulanır; herhangi bir
                doğrulama hatasında hiçbir değişiklik yapılmaz.
              </p>
            )}
            {plan.groups?.createTags?.length > 0 && (
              <div className="mt-3 text-xs">
                <p className="font-semibold">Oluşturulacak tag’ler</p>
                <p className="mt-1 text-body dark:text-bodydark">
                  {plan.groups.createTags.map(item => item.name).join(', ')}
                </p>
              </div>
            )}
            {plan.groups?.assignTags?.length > 0 && (
              <div className="mt-3 text-xs">
                <p className="font-semibold">Geliştirici atamaları</p>
                <ul className="mt-1 space-y-1 text-body dark:text-bodydark">
                  {plan.groups.assignTags.map((item, index) => (
                    <li key={`${plan.id}-assignment-${index}`}>
                      {item.developer}: {item.tags.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {plan.groups?.mergeDevelopers?.length > 0 && (
              <div className="mt-3 text-xs">
                <p className="font-semibold">Birleştirilecek geliştiriciler</p>
                <ul className="mt-1 space-y-1 text-body dark:text-bodydark">
                  {plan.groups.mergeDevelopers.map((item, index) => (
                    <li key={`${plan.id}-developer-merge-${index}`}>
                      {item.sourceDevelopers.join(', ')} →{' '}
                      {item.canonicalDeveloper}
                      {item.evidence?.length > 0 &&
                        ` · ${item.evidence
                          .map(evidence => evidence.value || evidence.kind)
                          .join(', ')}`}
                      {' · '}Metrikler asenkron yenilenecek
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {plan.groups?.assignRepositories?.length > 0 && (
              <div className="mt-3 text-xs">
                <p className="font-semibold">Repository atamaları</p>
                <ul className="mt-1 space-y-1 text-body dark:text-bodydark">
                  {plan.groups.assignRepositories.map((item, index) => (
                    <li key={`${plan.id}-repository-${index}`}>
                      {item.repository} → {item.project} ({item.operation})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {plan.groups?.deleteProjects?.length > 0 && (
              <div className="mt-3 text-xs">
                <p className="font-semibold">Silinecek boş projeler</p>
                <p className="mt-1 text-body dark:text-bodydark">
                  {plan.groups.deleteProjects.map(item => item.name).join(', ')}
                </p>
              </div>
            )}
            {plan.conflicts?.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-danger">
                {plan.conflicts.map((conflict, index) => (
                  <li key={`${plan.id}-conflict-${index}`}>
                    {conflict.message}
                  </li>
                ))}
              </ul>
            )}
            {plan.steps?.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setShowPlanDetails(current => !current)}
                  className="mt-3 text-xs font-medium text-primary hover:underline"
                >
                  {showPlanDetails ? 'Detayları gizle' : 'Tüm detayları göster'}
                </button>
                {showPlanDetails && (
                  <ol className="mt-3 space-y-1 text-xs text-body dark:text-bodydark">
                    {plan.steps.map((step, index) => (
                      <li key={step.id}>
                        <span className="font-medium">
                          {index + 1}. {step.display?.title || step.kind}
                        </span>
                        <span> — {stepStatusLabel(step.status)}</span>
                        {step.display?.description && (
                          <p>{step.display.description}</p>
                        )}
                        {step.error?.message && (
                          <p className="text-danger">{step.error.message}</p>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </>
            )}
            {plan.status === 'pending_approval' && (
              <button
                type="button"
                disabled={approvalBusy}
                onClick={() => onPlanApproval(message, plan)}
                className="mt-3 rounded bg-primary px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
              >
                {approvalBusy ? 'İşleniyor…' : 'Planı onayla'}
              </button>
            )}
            {plan.continuationAvailable && (
              <button
                type="button"
                disabled={approvalBusy}
                onClick={() => onPlanContinuation(message, plan)}
                className="mt-3 rounded border border-primary px-2.5 py-1 text-xs font-medium text-primary disabled:opacity-50"
              >
                {approvalBusy ? 'İşleniyor…' : 'Devam planı oluştur'}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const AiAssistant = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSessionId = searchParams.get('sessionId') || '';
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [starters, setStarters] = useState(DEFAULT_STARTERS);
  const [visibleStarters, setVisibleStarters] = useState(
    DEFAULT_STARTERS.slice(0, 3)
  );
  const [input, setInput] = useState('');
  const [sessionScope, setSessionScope] = useState('mine');
  const [sessionSearch, setSessionSearch] = useState('');
  const [historyPinned, setHistoryPinned] = useState(
    () => localStorage.getItem(HISTORY_PIN_STORAGE_KEY) === 'true'
  );
  const [historyOpen, setHistoryOpen] = useState(
    () => localStorage.getItem(HISTORY_PIN_STORAGE_KEY) === 'true'
  );
  const [starterInteractionActive, setStarterInteractionActive] =
    useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [error, setError] = useState('');
  const messageEndRef = useRef(null);
  const inputRef = useRef(null);
  const initializedSessionRef = useRef(false);
  const nextStarterIndexRef = useRef(3);
  const starterSlotRef = useRef(0);

  const starterCatalog = useMemo(
    () => mergeStarterCatalog(starters),
    [starters]
  );

  const canSend = useMemo(
    () => input.trim().length > 0 && !isSending,
    [input, isSending]
  );

  const scrollToBottom = useCallback(() => {
    window.requestAnimationFrame(() => {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending, scrollToBottom]);

  useEffect(() => {
    localStorage.setItem(HISTORY_PIN_STORAGE_KEY, String(historyPinned));
  }, [historyPinned]);

  useEffect(() => {
    if (activeSessionId) {
      window.sessionStorage.setItem(
        MINI_ASSISTANT_SESSION_STORAGE_KEY,
        activeSessionId
      );
    } else {
      window.sessionStorage.removeItem(MINI_ASSISTANT_SESSION_STORAGE_KEY);
    }
  }, [activeSessionId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener?.('change', updatePreference);
    return () => mediaQuery.removeEventListener?.('change', updatePreference);
  }, []);

  useEffect(() => {
    nextStarterIndexRef.current = 3;
    starterSlotRef.current = 0;
    setVisibleStarters(starterCatalog.slice(0, 3));
  }, [starterCatalog]);

  useEffect(() => {
    if (
      prefersReducedMotion ||
      starterInteractionActive ||
      starterCatalog.length <= 3
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setVisibleStarters(current => {
        const nextVisible = current.slice();
        let nextStarter = '';
        let attempts = 0;

        while (attempts < starterCatalog.length && !nextStarter) {
          const candidate =
            starterCatalog[nextStarterIndexRef.current % starterCatalog.length];
          nextStarterIndexRef.current += 1;
          attempts += 1;
          if (!nextVisible.includes(candidate)) nextStarter = candidate;
        }

        if (!nextStarter) return current;
        nextVisible[starterSlotRef.current] = nextStarter;
        starterSlotRef.current = (starterSlotRef.current + 1) % 3;
        return nextVisible;
      });
    }, STARTER_ROTATION_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [prefersReducedMotion, starterCatalog, starterInteractionActive]);

  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape' && historyOpen) setHistoryOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [historyOpen]);

  const startNewDraft = useCallback(() => {
    setActiveSessionId('');
    setMessages([]);
    setError('');
    setSearchParams({}, { replace: true });
    window.sessionStorage.removeItem(MINI_ASSISTANT_SESSION_STORAGE_KEY);
    window.setTimeout(() => inputRef.current?.focus(), 80);
  }, [setSearchParams]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setError('');
    try {
      const response = await sendRequest(
        buildSessionsUrl(sessionScope, sessionSearch),
        'GET'
      );
      const data = extractData(response);
      const nextSessions = data.sessions || [];
      setSessions(nextSessions);
      setStarters(data.starters || DEFAULT_STARTERS);
    } catch (err) {
      setError(
        err?.message || 'Ask AI could not load conversations. Please try again.'
      );
    } finally {
      setSessionsLoading(false);
    }
  }, [sessionScope, sessionSearch]);

  const loadMessages = useCallback(async sessionId => {
    if (!sessionId) return;
    setMessagesLoading(true);
    setError('');
    try {
      const response = await sendRequest(
        `/ai-assistant/sessions/${encodeURIComponent(sessionId)}/messages`,
        'GET'
      );
      const data = extractData(response);
      setMessages(data.messages || []);
      setStarters(data.starters || DEFAULT_STARTERS);
    } catch (err) {
      setError(
        err?.message ||
          'Ask AI could not load this conversation. Please try again.'
      );
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(loadSessions, 250);
    return () => window.clearTimeout(timeoutId);
  }, [loadSessions]);

  useEffect(() => {
    if (initializedSessionRef.current || !requestedSessionId) return;
    initializedSessionRef.current = true;
    setActiveSessionId(requestedSessionId);
  }, [requestedSessionId]);

  useEffect(() => {
    if (activeSessionId) {
      loadMessages(activeSessionId);
    }
  }, [activeSessionId, loadMessages]);

  const sendMessage = useCallback(
    async text => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      setInput('');
      setIsSending(true);
      setError('');
      setMessages(current => [...current, makeUserMessage(trimmed)]);

      try {
        const response = await sendRequest(
          getAssistantMessageEndpoint(activeSessionId),
          'POST',
          { message: trimmed }
        );
        const data = extractData(response);
        setMessages(current => [...current, makeAssistantMessage(data)]);
        setStarters(data.starters || DEFAULT_STARTERS);
        const nextSessionId =
          data.sessionId || data.session?.id || activeSessionId;
        if (!activeSessionId && nextSessionId) {
          setActiveSessionId(nextSessionId);
        }
        setSessions(current =>
          activeSessionId
            ? current.map(session =>
                session.id === activeSessionId
                  ? {
                      ...session,
                      ...(data.session || {}),
                      lastMessageAt:
                        data.generatedAt || new Date().toISOString()
                    }
                  : session
              )
            : data.session
              ? [
                  data.session,
                  ...current.filter(session => session.id !== data.session.id)
                ]
              : current
        );
      } catch (err) {
        setError(
          err?.message ||
            'Ask AI could not generate a response. Please try again.'
        );
      } finally {
        setIsSending(false);
        window.setTimeout(() => inputRef.current?.focus(), 80);
      }
    },
    [activeSessionId, isSending]
  );

  const decideApproval = useCallback(
    async (message, approval, approved) => {
      if (!activeSessionId || approvalBusy) return;
      setApprovalBusy(true);
      setError('');
      try {
        const response = await sendRequest(
          `/ai-assistant/sessions/${encodeURIComponent(activeSessionId)}/messages/${encodeURIComponent(message.id)}/approval`,
          'POST',
          { approvalId: approval.id, approved }
        );
        const data = extractData(response);
        setMessages(current =>
          current.map(item =>
            item.id === message.id
              ? makeAssistantMessage({ ...item, ...data })
              : item
          )
        );
        setStarters(data.starters || DEFAULT_STARTERS);
      } catch (err) {
        setError(err?.message || 'Ask AI could not process this approval.');
      } finally {
        setApprovalBusy(false);
      }
    },
    [activeSessionId, approvalBusy]
  );

  const approvePlan = useCallback(
    async (message, plan) => {
      if (!activeSessionId || approvalBusy || !plan?.id) return;
      setApprovalBusy(true);
      setError('');
      try {
        const response = await sendRequest(
          `/ai-assistant/sessions/${encodeURIComponent(activeSessionId)}/messages/${encodeURIComponent(message.id)}/plans/${encodeURIComponent(plan.id)}/approve`,
          'POST',
          {}
        );
        const data = extractData(response);
        setMessages(current =>
          current.map(item =>
            item.id === message.id
              ? makeAssistantMessage({ ...item, ...data })
              : item
          )
        );
      } catch (err) {
        setError(
          err?.message || 'Workspace action plan could not be completed.'
        );
      } finally {
        setApprovalBusy(false);
      }
    },
    [activeSessionId, approvalBusy]
  );

  const continuePlan = useCallback(
    async (message, plan) => {
      if (!activeSessionId || approvalBusy || !plan?.id) return;
      setApprovalBusy(true);
      setError('');
      try {
        const response = await sendRequest(
          `/ai-assistant/sessions/${encodeURIComponent(activeSessionId)}/messages/${encodeURIComponent(message.id)}/plans/${encodeURIComponent(plan.id)}/continuation`,
          'POST',
          {}
        );
        const data = extractData(response);
        setMessages(current =>
          current.map(item =>
            item.id === message.id
              ? makeAssistantMessage({ ...item, ...data })
              : item
          )
        );
      } catch (err) {
        setError(err?.message || 'Continuation plan could not be created.');
      } finally {
        setApprovalBusy(false);
      }
    },
    [activeSessionId, approvalBusy]
  );

  const renameSession = useCallback(async session => {
    const nextTitle = window.prompt(
      'Rename conversation',
      session.title || 'New conversation'
    );
    if (nextTitle === null) return;

    const trimmedTitle = nextTitle.trim();
    if (!trimmedTitle) return;

    try {
      const response = await sendRequest(
        `/ai-assistant/sessions/${encodeURIComponent(session.id)}`,
        'PATCH',
        { title: trimmedTitle }
      );
      const data = extractData(response);
      if (data.session) {
        setSessions(current =>
          current.map(item => (item.id === session.id ? data.session : item))
        );
      }
    } catch (err) {
      setError(
        err?.message ||
          'Ask AI could not rename this conversation. Please try again.'
      );
    }
  }, []);

  const deleteSession = useCallback(
    async session => {
      const confirmed = window.confirm(
        `Delete "${session.title || 'New conversation'}"?`
      );
      if (!confirmed) return;

      try {
        await sendRequest(
          `/ai-assistant/sessions/${encodeURIComponent(session.id)}`,
          'DELETE'
        );
        setSessions(current => current.filter(item => item.id !== session.id));
        if (activeSessionId === session.id) {
          const nextSession = sessions.find(item => item.id !== session.id);
          if (nextSession) {
            setActiveSessionId(nextSession.id);
            setMessages([]);
          } else {
            startNewDraft();
          }
        }
      } catch (err) {
        setError(
          err?.message ||
            'Ask AI could not delete this conversation. Please try again.'
        );
      }
    },
    [activeSessionId, sessions, startNewDraft]
  );

  const handleSubmit = event => {
    event.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage(input);
    }
  };

  const handleToggleHistoryPin = () => {
    setHistoryPinned(current => {
      const nextPinned = !current;
      if (nextPinned) setHistoryOpen(true);
      return nextPinned;
    });
  };

  const composer = (
    <MessageComposer
      input={input}
      inputRef={inputRef}
      canSend={canSend}
      onInputChange={event => setInput(event.target.value)}
      onKeyDown={handleKeyDown}
      onSubmit={handleSubmit}
      hero={messages.length === 0}
    />
  );

  const conversationHistory = (
    <SessionList
      sessions={sessions}
      activeSessionId={activeSessionId}
      loading={sessionsLoading}
      search={sessionSearch}
      scope={sessionScope}
      pinned={historyPinned}
      onSearchChange={setSessionSearch}
      onScopeChange={setSessionScope}
      onSelectSession={sessionId => {
        setActiveSessionId(sessionId);
        if (!historyPinned) setHistoryOpen(false);
      }}
      onCreateSession={startNewDraft}
      onRenameSession={renameSession}
      onDeleteSession={deleteSession}
      onClose={() => setHistoryOpen(false)}
      onTogglePin={handleToggleHistoryPin}
    />
  );

  return (
    <div className="relative h-[calc(100vh-12rem)] min-h-[680px] overflow-hidden rounded-xl border border-stroke bg-gray-2 shadow-sm dark:border-strokedark dark:bg-boxdark-2">
      {historyOpen && (
        <>
          <button
            type="button"
            className="absolute inset-0 z-20 bg-black/30 lg:hidden"
            onClick={() => setHistoryOpen(false)}
            aria-label="Close conversation history"
          />
        </>
      )}

      <div className="flex h-full min-w-0">
        {historyOpen && (
          <div
            className={`absolute inset-y-0 left-0 z-30 w-[min(22rem,calc(100%-2rem))] shadow-2xl lg:w-80 ${
              historyPinned
                ? 'lg:relative lg:inset-auto lg:z-auto lg:shrink-0 lg:shadow-none'
                : ''
            }`}
          >
            {conversationHistory}
          </div>
        )}
        <section className="flex min-w-0 flex-1 flex-col bg-white/60 dark:bg-boxdark">
          <header className="flex items-center justify-between gap-3 border-b border-stroke bg-white/90 px-5 py-3 backdrop-blur-sm dark:border-strokedark dark:bg-boxdark/90">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition dark:border-strokedark ${
                  historyOpen
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-stroke text-body hover:border-primary hover:text-primary dark:text-bodydark'
                }`}
                onClick={() => setHistoryOpen(current => !current)}
                aria-label={
                  historyOpen
                    ? 'Close conversation history'
                    : 'Open conversation history'
                }
                aria-expanded={historyOpen}
                title={
                  historyOpen
                    ? 'Close conversation history'
                    : 'Open conversation history'
                }
              >
                <Icons.Menu fontSize="small" />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold text-black dark:text-white">
                  Gitzy
                </h1>
                <p className="truncate text-xs text-body dark:text-bodydark">
                  Your engineering intelligence companion
                </p>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-stroke px-3 py-2 text-sm font-medium text-body transition hover:border-primary hover:text-primary dark:border-strokedark dark:text-bodydark"
              onClick={startNewDraft}
            >
              <Icons.Add fontSize="small" />
              <span className="hidden sm:inline">New chat</span>
            </button>
          </header>

          {error && (
            <div className="border-b border-danger/20 bg-danger/5 px-5 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8">
            {messagesLoading ? (
              <div className="mx-auto max-w-5xl space-y-4">
                {[0, 1, 2].map(item => (
                  <div
                    key={item}
                    className="h-24 animate-pulse rounded-xl bg-white dark:bg-boxdark"
                  />
                ))}
              </div>
            ) : messages.length === 0 ? (
              <EmptyConversation
                starters={visibleStarters}
                onStarterClick={sendMessage}
                onStarterInteractionChange={setStarterInteractionActive}
                composer={composer}
              />
            ) : (
              <div className="mx-auto max-w-[1320px] space-y-5">
                {messages.map(message => (
                  <div key={message.id} className="space-y-3">
                    {message.role === 'assistant' &&
                      message.artifacts?.length > 0 && (
                        <div className="w-full space-y-3">
                          {message.artifacts.map(artifact => (
                            <AssistantArtifactRenderer
                              key={artifact.id}
                              artifact={artifact}
                            />
                          ))}
                        </div>
                      )}
                    <AssistantMessage
                      message={message}
                      onApproval={decideApproval}
                      onPlanApproval={approvePlan}
                      onPlanContinuation={continuePlan}
                      approvalBusy={approvalBusy}
                    />
                  </div>
                ))}

                {isSending && (
                  <div className="flex justify-start">
                    <div className="rounded-xl border border-stroke bg-white px-4 py-3 text-sm text-body dark:border-strokedark dark:bg-boxdark dark:text-bodydark">
                      Gitzy is thinking...
                    </div>
                  </div>
                )}
                <div ref={messageEndRef} />
              </div>
            )}
          </div>

          {messages.length > 0 && (
            <footer className="border-t border-stroke bg-white/90 p-4 backdrop-blur-sm dark:border-strokedark dark:bg-boxdark/90">
              <div className="mx-auto max-w-[1320px]">
                {visibleStarters.length > 0 && (
                  <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                    {visibleStarters.map(starter => (
                      <button
                        key={starter}
                        type="button"
                        className="shrink-0 rounded-lg border border-stroke px-3 py-1.5 text-xs text-body transition hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-bodydark"
                        onClick={() => sendMessage(starter)}
                        disabled={isSending}
                      >
                        {starter}
                      </button>
                    ))}
                  </div>
                )}
                {composer}
              </div>
            </footer>
          )}
        </section>
      </div>
    </div>
  );
};

export default AiAssistant;
